<template>
  <div class="terms-container">
    <div class="terms-header my-3">
      <h2 class="weight-500">Terms and Conditions</h2>
    </div>

    <div class="terms-content">
      <!-- File Upload -->
      <div class="terms-section mb-4">
        <label class="terms-label">Content</label>
        <div
          class="file-upload-area"
          @click="triggerFileInput"
          @dragover.prevent
          @drop.prevent="handleFileDrop"
          :class="{ uploading: isUploading }"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".pdf,.doc,.docx"
            @change="handleFileChange"
            style="display: none;"
          />
<div class="upload-content">
  <span v-if="isUploading" class="loader-small"></span>
  <svg
    v-else
    class="upload-icon"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
  <span v-if="isUploading" class="uploading-text">
    Uploading...
  </span>
  <span v-else-if="uploadedFile" class="file-name">
    {{ uploadedFile.name }}
  </span>
  <span v-else>
    Click to upload or drag and drop file
  </span>
          </div>
        </div>
      </div>
      <!-- Comments -->
      <div class="terms-section mb-4">
        <label class="terms-label">Description</label>
        <textarea
          v-model="localTermsData.comments"
          class="terms-textarea"
          placeholder="Add comments or notes..."
          rows="3"
          :disabled="isUploading"
        />
      </div>

      <!-- History -->
      <div class="terms-section mb-4">
        <label class="terms-label mb-2">History</label>
        <div class="copy-dtx-table">
          <table>
            <thead>
              <tr>
                <th>Version</th>
                <th>Date</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr
  v-for="(item, index) in historyData" :key="index" class="history-row" @click="$emit('view-history', item)">
                <td>{{ item.version }}</td>
                <td>{{ item.date }}</td>
                <td>{{ item.description }}</td>
              </tr>
              <tr v-if="!historyData.length">
                <td colspan="3" class="no-data">No history available</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Actions -->
      <div class="terms-actions">
        <button class="btn btn-black ml-3" @click="handleCancel" :disabled="isUploading">
          <span v-if="isUploading" class="loader-small"></span>
          <span v-else>Cancel</span>
        </button>
        <button class="btn ml-3" @click="handlePublish" :disabled="isUploading">
          <span v-if="isUploading" class="loader-small"></span>
          <span v-else>Publish</span>
        </button>
      </div>
    </div>

    <!-- PDF Modal -->
    <modal :open="showPdfModal" @close="showPdfModal = false" title="Current Terms and Conditions" :scroll="false">
      <div class="pdf-viewer-container">
        <div v-if="loadingPdf" class="pdf-loader">
          <span class="loader-small"></span>
          <p>Loading PDF...</p>
        </div>
        <iframe
          v-else-if="currentPdfUrl"
          :src="currentPdfUrl"
          frameborder="0"
          @load="loadingPdf = false"
        />
      </div>
    </modal>
  </div>
</template>

<script>
import { alert } from '@/plugins/notification'
import modal from './modal.vue'

export default {
  name: 'TermsAndConditions',
  components: { modal },
  props: {
    termsData: {
      type: Object,
      default: () => ({
        version: '',
        comments: '',
        fileUrl: null
      })
    },
    historyData: {
      type: Array,
      default: () => []
    },
    isUploading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      localTermsData: {
        version: '',
        comments: '',
        fileUrl: null
      },
      uploadedFile: null,
      showPdfModal: false,
      currentPdfUrl: '',
      loadingPdf: false
    }
  },
  watch: {
    termsData: {
      handler (val) {
        this.localTermsData = { ...val }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    async loadPdfFromSignedUrl (signedUrl) {
      try {
        this.loadingPdf = true

        if (this.currentPdfUrl) {
          URL.revokeObjectURL(this.currentPdfUrl)
          this.currentPdfUrl = ''
        }

        const response = await fetch(signedUrl)
        const blob = await response.blob()
        this.currentPdfUrl = URL.createObjectURL(blob.slice(0, blob.size, 'application/pdf'))
        this.showPdfModal = true
      } catch (e) {
        console.error(e)
        alert('Failed to load PDF')
      } finally {
        this.loadingPdf = false
      }
    },

    clearForm () {
      this.uploadedFile = null
      this.localTermsData = { version: '', comments: '', fileUrl: null }
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    triggerFileInput () {
      if (!this.isUploading) this.$refs.fileInput.click()
    },

    handleFileChange (e) {
      this.validateAndSetFile(e.target.files[0])
    },

    handleFileDrop (e) {
      if (!this.isUploading) {
        this.validateAndSetFile(e.dataTransfer.files[0])
      }
    },

    validateAndSetFile (file) {
      if (!file) return
      const ext = '.' + file.name.split('.').pop().toLowerCase()
      if (!['.pdf', '.doc', '.docx'].includes(ext)) {
        alert('Only PDF, DOC, and DOCX files are allowed')
        return
      }
      this.uploadedFile = file
      // this.$emit('upload-file', file)
    },

    handlePublish () {
      if (!this.uploadedFile && !this.localTermsData.fileUrl) {
        alert('Please upload a file')
        return
      }
      this.$emit('publish', {
        comments: this.localTermsData.comments,
        file: this.uploadedFile
      })
    },
    handleCancel () {
      if (this.isUploading) return

      this.uploadedFile = null
      this.localTermsData.comments = ''

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    }

  },
  beforeDestroy () {
    if (this.currentPdfUrl) {
      URL.revokeObjectURL(this.currentPdfUrl)
    }
  }
}
</script>

<style lang="scss" scoped>
.loader-small {
  width: 16px;
  height: 16px;
  border: 2px solid #ccc;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.terms-container {
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: calc(100vh - 350px);
  overflow-y: auto;
}

.terms-header {
  h2 {
    font-size: var(--text-3xl);
    font-weight: 500;
  }
}

.terms-content {
  max-width: 900px;
  width: 100%;
}

.terms-section {
  margin-bottom: 24px;
}

.terms-label {
  display: block;
  font-size: var(--text-l);
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.file-upload-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;

  &:hover:not(.uploading) {
    border-color: #999;
    background-color: #f5f5f5;
  }

  &.uploading {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #666;
}

.upload-icon {
  color: #999;
}

.file-name {
  color: #333;
  font-weight: 500;
}

.uploading-text {
  color: #3498db;
  font-weight: 500;
}

.terms-row {
  display: flex;
  gap: 20px;
  align-items: flex-end;
}

.version-section {
  flex: 1;
  margin-bottom: 0;
}

.view-section {
  margin-bottom: 0;
}

.terms-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: var(--text-base);
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--brand-color);
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
}

.terms-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: var(--text-base);
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
  max-height: 120px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--brand-color);
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
}

.btn-view {
  padding: 10px 24px;
  background-color: white;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: var(--text-base);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
    border-color: #999;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.no-data {
  text-align: center;
  color: #888;
  padding: 12px;
}

.terms-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
}

.btn-secondary {
  padding: 10px 24px;
  background-color: white;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: var(--text-base);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
    border-color: #999;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.btn-primary {
  padding: 10px 24px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: var(--text-base);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.pdf-viewer-container {
  width: 85vw;
  max-width: 95%;
  height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
}

.pdf-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  p {
    color: #666;
    font-size: 0.875rem;
  }
}
</style>
